import {
  ACCOUNT, ME, PEOPLE, PATIENTS, DIAGNOSES, PRIMARY_TAGS, BODY_SITES, SETTINGS, buildSeed,
} from '@/api/mock/mediaSample';

/**
 * Patient media (photos, videos, sets, progressions), one function per keyon method to come.
 *
 * Every layout of the media page (A, B, C) reaches media only through here, so moving a kind from
 * the browser to keyon changes this module and nothing in a layout. Each function is named after
 * the `Media` API_Meth it becomes; until then it answers from a mock kept in this browser:
 * metadata in localStorage under `mediaProto:<kind>`, uploaded bytes in IndexedDB (`mediaProto`
 * database), seed files under /media-proto/ (generated illustrations, not patient photos). The
 * patients are a dictionary under `mediaProto:patients`, so media can be added to any of them.
 *
 * The mock enforces the rules the backend will: who may see what (MediaAccess, modelled on
 * keyon/lib/NoteAccess.php), one patient per set and progression, the reassign split check,
 * soft removal with the uploader's window, frozen references from notes and messages.
 *
 * Prototype only: `setViewer()` switches between the caregiver and the patient John Doe, so a
 * reviewer can see what a patient is shown. In production the session decides.
 *
 * Main's keyon transport for the kinds that leave the mock (`keyonCall`, a `Media` envelope to
 * tswebhook.php) is not carried: nothing called it, and the port has no `Media` operation behind the
 * bridge. A kind that moves to the server goes through a native api2 client, as pre-pour's did.
 */

/** A refusal: `code` is what keyon will answer (forbidden, invalid, would_split, not_found, reason_required). */
export class MediaError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.code = code;
    Object.assign(this, extra);
  }
}

/* ------------------------------------------------------------------ storage */

const KINDS = ['items', 'sets', 'progressions', 'sources', 'reassignLog', 'meta'];
const memory = {};

function readKind(kind) {
  try {
    const raw = window.localStorage.getItem(`mediaProto:${kind}`);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* private mode or bad JSON: fall back to memory */ }
  return memory[kind];
}

function writeKind(kind, value) {
  memory[kind] = value;
  try { window.localStorage.setItem(`mediaProto:${kind}`, JSON.stringify(value)); } catch (e) { /* memory only */ }
}

function db() {
  let items = readKind('items');
  if (!items) {
    const seed = buildSeed();
    writeKind('items', seed.items);
    writeKind('sets', seed.sets);
    writeKind('progressions', seed.progressions);
    writeKind('sources', seed.sources);
    writeKind('reassignLog', seed.reassignLog);
    writeKind('meta', { nextId: seed.nextId });
    items = seed.items;
  }
  return {
    items,
    sets: readKind('sets') || [],
    progressions: readKind('progressions') || [],
    sources: readKind('sources') || [],
    reassignLog: readKind('reassignLog') || [],
    meta: readKind('meta') || { nextId: 1000 },
  };
}

function save(state) {
  KINDS.forEach((k) => writeKind(k, state[k]));
}

/*
 * The roster: a dictionary keyed by unique_ID ({ pid, unique_ID, first_name, lastname,
 * date_of_birth, gender }), seeded with the sample patients. The chart that opens the page adds its
 * patient (addMediaPatient), so media can be added to real chart patients as well.
 */
const PATIENTS_KEY = 'patients';

function patients() {
  let dict = readKind(PATIENTS_KEY);
  if (!dict) {
    dict = Object.fromEntries(PATIENTS.map((p) => [p.unique_ID, p]));
    writeKind(PATIENTS_KEY, dict);
  }
  return dict;
}

function nextId(state) {
  state.meta.nextId += 1;
  return state.meta.nextId;
}

// Uploaded bytes. IndexedDB keeps them across reloads; a Map is the fallback (lost on reload).
const blobMemory = new Map();
const urlCache = new Map();
let idbPromise = null;

function idb() {
  if (idbPromise) return idbPromise;
  idbPromise = new Promise((resolve) => {
    try {
      const req = window.indexedDB.open('mediaProto', 1);
      req.onupgradeneeded = () => req.result.createObjectStore('blobs');
      // Sign-out deletes this database (auth/oidcClient clearSession); an open connection would hold that back.
      req.onsuccess = () => { req.result.onversionchange = () => req.result.close(); resolve(req.result); };
      req.onerror = () => resolve(null);
    } catch (e) { resolve(null); }
  });
  return idbPromise;
}

async function putBlob(key, blob) {
  blobMemory.set(key, blob);
  const d = await idb();
  if (!d) return;
  await new Promise((resolve) => {
    try {
      const tx = d.transaction('blobs', 'readwrite');
      tx.objectStore('blobs').put(blob, key);
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    } catch (e) { resolve(); }
  });
}

async function getBlob(key) {
  if (blobMemory.has(key)) return blobMemory.get(key);
  const d = await idb();
  if (!d) return null;
  return new Promise((resolve) => {
    try {
      const req = d.transaction('blobs').objectStore('blobs').get(key);
      req.onsuccess = () => { if (req.result) blobMemory.set(key, req.result); resolve(req.result || null); };
      req.onerror = () => resolve(null);
    } catch (e) { resolve(null); }
  });
}

/* ------------------------------------------------------------------ viewer + access */

let viewer = { kind: 'caregiver', ...ME };

/** Prototype only: 'caregiver' (Jennifer Smith, RN) or 'patient' (John Doe viewing his own media). */
export function setViewer(kind) {
  viewer = kind === 'patient'
    ? { kind: 'patient', unique_ID: PATIENTS[0].unique_ID, name: `${PATIENTS[0].first_name} ${PATIENTS[0].lastname}` }
    : { kind: 'caregiver', ...ME };
  return getViewer();
}

export function getViewer() {
  return { ...viewer, can_manage: viewer.kind === 'caregiver' };
}

// The mock roster stands in for the caregiver's own Patients groups (ChartAccess owned_group).
const chartPatients = () => (viewer.kind === 'patient' ? [viewer.unique_ID] : Object.keys(patients()));

/** MediaAccess.canView: chart access + view_media; a patient only sees items marked patient-visible. */
function canViewItem(it) {
  if (it.patient_unique_id === null) return viewer.kind === 'caregiver' && it.author_unique_id === viewer.unique_ID;
  if (!chartPatients().includes(it.patient_unique_id)) return false;
  if (viewer.kind === 'patient') return !!it.patient_visible;
  return true;
}

function requireManage() {
  if (viewer.kind !== 'caregiver') throw new MediaError('forbidden', 'Only caregivers with manage_media can change media.');
}

function requireChart(patientUid) {
  if (patientUid !== null && !chartPatients().includes(patientUid)) {
    throw new MediaError('forbidden', 'You have no access to that patient’s chart.');
  }
}

/* ------------------------------------------------------------------ shaping */

const live = (row) => !row.removed_at;
const personName = (uid) => PEOPLE[uid] || uid;
const patientOf = (uid) => (uid && patients()[uid]) || null;
export const patientName = (uid) => {
  const p = patientOf(uid);
  return p ? `${p.first_name} ${p.lastname}` : 'My library';
};

function diagnosisOf(it) {
  if (!it.orddiagid || !it.patient_unique_id) return null;
  return (DIAGNOSES[it.patient_unique_id] || []).find((d) => d.orddiagid === it.orddiagid) || null;
}

function shapeItem(it) {
  return {
    ...it,
    author_name: personName(it.author_unique_id),
    captured_by_name: it.captured_by_unique_id ? personName(it.captured_by_unique_id) : null,
    patient_name: patientName(it.patient_unique_id),
    diagnosis: diagnosisOf(it),
  };
}

function setItems(state, set) {
  return set.items
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((m) => state.items.find((it) => it.id === m.media_id))
    .filter((it) => it && live(it));
}

function canViewSet(state, set) {
  if (!live(set)) return false;
  const members = setItems(state, set);
  if (set.patient_unique_id === null) return viewer.kind === 'caregiver' && set.owner_unique_id === viewer.unique_ID;
  if (!chartPatients().includes(set.patient_unique_id)) return false;
  // The patient sees a set only when every item in it is patient-visible.
  if (viewer.kind === 'patient') return members.length > 0 && members.every((it) => it.patient_visible);
  return true;
}

function shapeSet(state, set) {
  const members = setItems(state, set);
  return {
    ...set,
    items: set.items.slice().sort((a, b) => a.position - b.position),
    media: members.map(shapeItem),
    count: members.length,
    cover: members[0] ? shapeItem(members[0]) : null,
    owner_name: personName(set.owner_unique_id),
    patient_name: patientName(set.patient_unique_id),
    progression_name: set.progression_id
      ? (state.progressions.find((p) => p.id === set.progression_id) || {}).name || null
      : null,
  };
}

function shapeProgression(state, p) {
  const sets = state.sets
    .filter((s) => s.progression_id === p.id && canViewSet(state, s))
    .sort((a, b) => a.set_date.localeCompare(b.set_date))
    .map((s) => shapeSet(state, s));
  return {
    ...p,
    sets,
    count: sets.length,
    cover: sets.length ? sets[sets.length - 1].cover : null,
    latest_date: sets.length ? sets[sets.length - 1].set_date : null,
    patient_name: patientName(p.patient_unique_id),
  };
}

function itemMatches(it, f) {
  if (f.kind === 'image' && it.kind !== 'image') return false;
  if (f.kind === 'video' && it.kind !== 'video') return false;
  if (f.primary_tag && it.primary_tag !== f.primary_tag) return false;
  if (f.tag && !(it.tags || []).includes(f.tag)) return false;
  if (f.author && it.author_unique_id !== f.author) return false;
  if (f.body_site && it.body_site !== f.body_site) return false;
  if (f.orddiagid && it.orddiagid !== Number(f.orddiagid)) return false;
  if (f.date_from && it.captured_at.slice(0, 10) < f.date_from) return false;
  if (f.date_to && it.captured_at.slice(0, 10) > f.date_to) return false;
  return true;
}

// A progression shows under a filter when one of its sets has an item that passes it.
const progressionMatches = (p, f) => p.sets.some((s) => s.media.some((it) => itemMatches(it, { ...f, kind: 'all' })));

const byDate = (sort) => (a, b) => {
  const da = a.captured_at || a.set_date || a.latest_date || '';
  const dbb = b.captured_at || b.set_date || b.latest_date || '';
  return sort === 'oldest' ? da.localeCompare(dbb) : dbb.localeCompare(da);
};

const tick = () => new Promise((r) => setTimeout(r, 60));

/* ------------------------------------------------------------------ reads */

/** Mock roster. In production: the patients the session's groups reach (GetPatientsFromStaffPatientsGroup). */
export async function listMediaPatients() {
  await tick();
  return Object.values(patients())
    .filter((p) => chartPatients().includes(p.unique_ID))
    .map((p) => ({ ...p, name: `${p.first_name} ${p.lastname}`.trim() }));
}

/**
 * Prototype: adds (or refreshes) a patient in the roster, e.g. the chart patient who opened the
 * page. Their pid stays null: the chart session carries psersonalID, not patients.pid. In
 * production the chart patient is already in the caregiver's groups and nothing is added.
 */
export async function addMediaPatient(patient) {
  if (!patient || !patient.unique_ID) return;
  const dict = patients();
  dict[patient.unique_ID] = { pid: null, ...dict[patient.unique_ID], ...patient };
  writeKind(PATIENTS_KEY, dict);
}

/** Future API_Meth GetMediaConfig: media_primary_tag, media_body_site, media_setting. */
export async function loadMediaConfig() {
  await tick();
  return { primary_tags: PRIMARY_TAGS, body_sites: BODY_SITES, settings: SETTINGS, people: PEOPLE };
}

/** Future: PopulateDiagGrid (orders_diagnosis) for the diagnosis picker. */
export async function listPatientDiagnoses(patientUid) {
  await tick();
  return DIAGNOSES[patientUid] || [];
}

/** Secondary-tag suggestions: tags already used in the org. */
export async function listTagSuggestions() {
  const state = db();
  const counts = {};
  state.items.filter(live).forEach((it) => (it.tags || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
}

/**
 * Future API_Meth ListPatientMedia { patient_uid, library?, filters }. `patientUid` null = the
 * viewer's personal library. Removed media is left out.
 */
export async function listPatientMedia(patientUid, filters = {}) {
  await tick();
  requireChart(patientUid);
  const state = db();
  const f = { kind: 'all', sort: 'newest', ...filters };
  const mine = patientUid === null;
  const items = state.items
    .filter((it) => live(it) && it.patient_unique_id === patientUid && canViewItem(it) && itemMatches(it, f))
    .filter((it) => !mine || it.author_unique_id === viewer.unique_ID)
    .map(shapeItem)
    .sort(byDate(f.sort));
  const sets = state.sets
    .filter((s) => s.patient_unique_id === patientUid && canViewSet(state, s))
    .map((s) => shapeSet(state, s))
    .filter((s) => s.media.some((it) => itemMatches(it, { ...f, kind: 'all' })))
    .sort(byDate(f.sort));
  const progressions = state.progressions
    .filter((p) => live(p) && p.patient_unique_id === patientUid)
    .map((p) => shapeProgression(state, p))
    .filter((p) => p.count > 0 && progressionMatches(p, f))
    .sort(byDate(f.sort));
  return { items, sets, progressions, total: items.length };
}

/** Future API_Meth GetMediaItem. */
export async function getMediaItem(id) {
  await tick();
  const it = db().items.find((x) => x.id === id);
  if (!it || !canViewItem(it)) throw new MediaError('not_found', 'That item is not available.');
  return shapeItem(it);
}

/** Future API_Meth GetMediaSet. */
export async function getMediaSet(id) {
  await tick();
  const state = db();
  const s = state.sets.find((x) => x.id === id);
  if (!s || !canViewSet(state, s)) throw new MediaError('not_found', 'That set is not available.');
  return shapeSet(state, s);
}

/** Future API_Meth GetProgression: sets ordered by set date. */
export async function getProgression(id) {
  await tick();
  const state = db();
  const p = state.progressions.find((x) => x.id === id);
  if (!p || !live(p) || !chartPatients().includes(p.patient_unique_id)) throw new MediaError('not_found', 'That progression is not available.');
  return shapeProgression(state, p);
}

/**
 * Notes and messages for a patient with the media they reference. Future: GetAllNotes /
 * the message thread plus GetMediaRefs. References are frozen: the note shows the items it was
 * written with, even if a set changed or an item was removed from the library since.
 */
export async function listReferencingSources(patientUid) {
  await tick();
  requireChart(patientUid);
  const state = db();
  return state.sources
    .filter((s) => s.patient_unique_id === patientUid)
    .map((s) => {
      const media = s.frozen_item_ids
        .map((id) => state.items.find((it) => it.id === id))
        .filter((it) => it && canViewItem(it))
        .map(shapeItem);
      const set = s.set_id ? state.sets.find((x) => x.id === s.set_id) : null;
      const setChanged = !!set && JSON.stringify(set.items.slice().sort((a, b) => a.position - b.position).map((m) => m.media_id)) !== JSON.stringify(s.frozen_item_ids);
      const files = (s.message_files || []).filter(() => viewer.kind === 'caregiver');
      return { ...s, media, message_files: files, set_name: set ? set.name : null, set_changed: setChanged };
    })
    .filter((s) => s.media.length || s.message_files.length)
    .sort((a, b) => b.postDate.localeCompare(a.postDate));
}

/** A displayable URL for an item's full file or thumbnail. Uploaded bytes come from IndexedDB. */
export async function getMediaUrl(item, which = 'full') {
  const staticSrc = which === 'thumb' ? item.thumb_src || item.src : item.src;
  if (staticSrc) return staticSrc;
  if (item.seed_without_bytes) return null;
  const key = `${item.public_id}:${which}`;
  if (urlCache.has(key)) return urlCache.get(key);
  const blob = await getBlob(key);
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  urlCache.set(key, url);
  return url;
}

/* ------------------------------------------------------------------ search */

/**
 * Future API_Meth SearchMedia. Mirrors MySQL FULLTEXT in boolean mode over item notes and tags, and
 * set / progression names: words must all match, `word*` is a prefix, a leading `*` is not supported
 * and is dropped with a hint. scope: 'patient' | 'all' (patients reachable through the viewer's
 * own Patients groups) | 'mine' (personal library). Access is applied before counting.
 */
export async function searchMedia(query = {}) {
  await tick();
  const state = db();
  const q = { scope: 'patient', kind: 'all', sort: 'newest', text: '', ...query };
  let hint = null;
  // The query splits into words the way the indexed text does (at spaces, hyphens, punctuation),
  // so a hyphenated tag like "post-op" still matches.
  const terms = (q.text || '').toLowerCase().split(/[^a-z0-9*]+/).filter(Boolean).map((t) => {
    if (t.startsWith('*')) { hint = 'A * at the start of a word is not supported — use it at the end (bruis*).'; t = t.replace(/^\*+/, ''); }
    return t.endsWith('*') ? { prefix: true, w: t.replace(/\*+$/, '') } : { prefix: false, w: t };
  }).filter((t) => t.w);
  const words = (s) => (s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const textMatch = (s) => !terms.length || terms.every((t) => words(s).some((w) => (t.prefix ? w.startsWith(t.w) : w === t.w)));
  // People search for what they tagged ("bruise"), not only what they wrote. FULLTEXT can't reach a
  // joined tag table, so the backend needs the tags in the indexed text (a tags column on the item).
  const itemText = (it) => [it.note, ...(it.tags || [])].join(' ');

  const patients = q.scope === 'mine' ? [null] : q.scope === 'all' ? chartPatients() : [q.patient_unique_id];
  if (q.scope === 'all' && viewer.kind === 'patient') throw new MediaError('forbidden', 'Patients search their own media only.');
  const inScope = (uid) => patients.includes(uid);
  const results = [];

  if (q.kind === 'all' || q.kind === 'image' || q.kind === 'video') {
    state.items
      .filter((it) => live(it) && inScope(it.patient_unique_id) && canViewItem(it) && itemMatches(it, q) && textMatch(itemText(it)))
      .filter((it) => it.patient_unique_id !== null || it.author_unique_id === viewer.unique_ID)
      .forEach((it) => results.push({ type: 'item', id: it.id, title: it.note || (it.kind === 'video' ? 'Video' : 'Image'), date: it.captured_at, patient_unique_id: it.patient_unique_id, patient_name: patientName(it.patient_unique_id), cover: shapeItem(it), count: 1, tags: it.tags, primary_tag: it.primary_tag, kind: it.kind }));
  }
  if (q.kind === 'all' || q.kind === 'set') {
    state.sets
      .filter((s) => inScope(s.patient_unique_id) && canViewSet(state, s) && textMatch(s.name))
      .map((s) => shapeSet(state, s))
      .filter((s) => s.media.some((it) => itemMatches(it, { ...q, kind: 'all' })))
      .forEach((s) => results.push({ type: 'set', id: s.id, title: s.name, date: s.set_date, patient_unique_id: s.patient_unique_id, patient_name: s.patient_name, cover: s.cover, count: s.count, tags: s.cover ? s.cover.tags : [], primary_tag: s.cover ? s.cover.primary_tag : null }));
  }
  if (q.kind === 'all' || q.kind === 'progression') {
    state.progressions
      .filter((p) => live(p) && inScope(p.patient_unique_id) && textMatch(p.name))
      .map((p) => shapeProgression(state, p))
      .filter((p) => p.count && progressionMatches(p, q))
      .forEach((p) => results.push({ type: 'progression', id: p.id, title: p.name, date: p.latest_date, patient_unique_id: p.patient_unique_id, patient_name: p.patient_name, cover: p.cover, count: p.count, tags: [], primary_tag: p.primary_tag }));
  }
  const sorted = q.sort === 'patient'
    ? results.sort((a, b) => a.patient_name.localeCompare(b.patient_name) || (b.date || '').localeCompare(a.date || ''))
    : q.sort === 'diagnosis'
      ? results.sort((a, b) => ((a.cover && a.cover.diagnosis && a.cover.diagnosis.diagnosis_code) || 'zzz').localeCompare((b.cover && b.cover.diagnosis && b.cover.diagnosis.diagnosis_code) || 'zzz'))
      : results.sort((a, b) => (q.sort === 'oldest' ? (a.date || '').localeCompare(b.date || '') : (b.date || '').localeCompare(a.date || '')));
  return { results: sorted.slice(0, 30), total: sorted.length, hint };
}

/* ------------------------------------------------------------------ writes */

const normTag = (t) => String(t || '').trim().toLowerCase();
// "Captured by" is kept only when someone other than the uploader took it.
const takenBy = (uid, authorUid = viewer.unique_ID) => (uid && uid !== authorUid ? uid : null);

/**
 * Future API_Meth CommitMediaBatch. Step two of the two-step upload: the files were processed
 * in the browser (see utils/mediaPipeline.js); this attaches them all at once to a patient (or the
 * personal library), with note, tags, optional new set and optional note reference.
 */
export async function commitMediaBatch(batch) {
  await tick();
  requireManage();
  const patientUid = batch.patient_unique_id === undefined ? null : batch.patient_unique_id;
  requireChart(patientUid);
  if (!batch.files || !batch.files.length) throw new MediaError('invalid', 'Nothing to upload.');
  const state = db();
  const p = patientOf(patientUid);
  const created = [];
  for (const f of batch.files) {
    const id = nextId(state);
    const publicId = `up${String(id).padStart(6, '0')}${Math.random().toString(16).slice(2, 26).padEnd(24, '0')}`;
    await putBlob(`${publicId}:full`, f.full);
    await putBlob(`${publicId}:thumb`, f.thumb);
    const row = {
      id,
      public_id: publicId,
      accountnumber: ACCOUNT,
      patient_pid: p ? p.pid : null,
      patient_unique_id: patientUid,
      author_unique_id: viewer.unique_ID,
      captured_by_unique_id: takenBy(batch.captured_by_unique_id),
      kind: f.kind,
      content_type: f.content_type,
      byte_size: f.byte_size,
      original_filename: f.original_filename || null,
      width: f.width || null,
      height: f.height || null,
      duration_sec: f.duration_sec || null,
      captured_at: f.captured_at,
      capture_source: f.capture_source || 'gallery',
      note: (f.note !== undefined ? f.note : batch.note) || '',
      primary_tag: batch.primary_tag || null,
      tags: [...new Set((batch.tags || []).map(normTag).filter(Boolean))],
      body_site: batch.body_site || null,
      body_site_detail: batch.body_site_detail || null,
      orddiagid: batch.orddiagid ? Number(batch.orddiagid) : null,
      patient_visible: batch.patient_visible ? 1 : 0,
      created_at: new Date().toISOString(),
      removed_at: null,
      removed_by_unique_id: null,
      remove_reason: null,
      src: null,
      thumb_src: null,
    };
    state.items.push(row);
    created.push(row);
  }
  let set = null;
  if (batch.create_set && batch.create_set.name && patientUid !== undefined) {
    set = newSetRow(state, patientUid, batch.create_set.name, created);
  }
  let source = null;
  if (batch.source && patientUid) {
    source = {
      source_type: 'note', source_id: nextId(state), patient_unique_id: patientUid,
      postDate: new Date().toISOString(), provsignature: viewer.name, type: 'Progress',
      pnotes: batch.source.pnotes || '', frozen_item_ids: created.map((r) => r.id), set_id: set ? set.id : null,
    };
    state.sources.push(source);
  }
  save(state);
  return { items: created.map(shapeItem), set: set ? shapeSet(state, set) : null, source };
}

function newSetRow(state, patientUid, name, members) {
  const p = patientOf(patientUid);
  const earliest = members.map((m) => m.captured_at).sort()[0] || new Date().toISOString();
  const row = {
    id: nextId(state),
    accountnumber: ACCOUNT,
    patient_pid: p ? p.pid : null,
    patient_unique_id: patientUid,
    owner_unique_id: viewer.unique_ID,
    name: String(name).trim().slice(0, 150),
    set_date: earliest.slice(0, 10),
    progression_id: null,
    created_at: new Date().toISOString(),
    removed_at: null,
    items: members.map((m, i) => ({ media_id: m.id, position: i + 1 })),
  };
  state.sets.push(row);
  return row;
}

const EDITABLE = ['note', 'primary_tag', 'tags', 'body_site', 'body_site_detail', 'orddiagid', 'patient_visible', 'captured_by_unique_id'];

/** Future API_Meth UpdateMediaItem: the bytes never change, only these fields. */
export async function updateMediaItem(id, fields) {
  await tick();
  requireManage();
  const state = db();
  const it = state.items.find((x) => x.id === id);
  if (!it || !canViewItem(it)) throw new MediaError('not_found', 'That item is not available.');
  EDITABLE.forEach((k) => {
    if (fields[k] === undefined) return;
    if (k === 'tags') it.tags = [...new Set(fields.tags.map(normTag).filter(Boolean))];
    else if (k === 'patient_visible') it.patient_visible = fields.patient_visible ? 1 : 0;
    else if (k === 'orddiagid') it.orddiagid = fields.orddiagid ? Number(fields.orddiagid) : null;
    else if (k === 'captured_by_unique_id') it.captured_by_unique_id = takenBy(fields.captured_by_unique_id, it.author_unique_id);
    else it[k] = fields[k];
  });
  save(state);
  return shapeItem(it);
}

/**
 * Future API_Meth RemoveMedia. Soft: hides from the library, never deletes bytes. The uploader
 * may remove their own item within the window without a reason; anyone else with manage_media must
 * give one. Removing an item takes it out of every set; removing a set leaves its items.
 * Media a note references stays visible in that note.
 */
export async function removeMedia(target, reason = '') {
  await tick();
  requireManage();
  const state = db();
  const now = new Date().toISOString();
  if (target.type === 'item') {
    const it = state.items.find((x) => x.id === target.id);
    if (!it || !live(it) || !canViewItem(it)) throw new MediaError('not_found', 'That item is not available.');
    const inWindow = it.author_unique_id === viewer.unique_ID
      && Date.now() - new Date(it.created_at).getTime() < SETTINGS.uploader_remove_window_sec * 1000;
    if (!inWindow && !String(reason).trim()) throw new MediaError('reason_required', 'Give a reason for removing this item.');
    Object.assign(it, { removed_at: now, removed_by_unique_id: viewer.unique_ID, remove_reason: String(reason).trim() || 'Removed by uploader' });
    state.sets.forEach((s) => {
      const before = s.items.length;
      s.items = s.items.filter((m) => m.media_id !== it.id).map((m, i) => ({ ...m, position: i + 1 }));
      if (s.items.length !== before) s.updated_at = now;
    });
    save(state);
    return { removed: [{ type: 'item', id: it.id }], purge_after_years: SETTINGS.retention_years };
  }
  if (target.type === 'set') {
    const s = state.sets.find((x) => x.id === target.id);
    if (!s || !live(s)) throw new MediaError('not_found', 'That set is not available.');
    if (!String(reason).trim() && s.owner_unique_id !== viewer.unique_ID) throw new MediaError('reason_required', 'Give a reason for removing this set.');
    Object.assign(s, { removed_at: now, removed_by_unique_id: viewer.unique_ID, remove_reason: String(reason).trim() || 'Removed by owner' });
    save(state);
    return { removed: [{ type: 'set', id: s.id }] };
  }
  throw new MediaError('invalid', 'Unknown target.');
}

/**
 * Future API_Meth SaveMediaSet: create, rename, set date, add items, attach to a progression.
 * Every item and the progression must belong to the set's patient.
 */
export async function saveMediaSet(input) {
  await tick();
  requireManage();
  const state = db();
  let s = input.id ? state.sets.find((x) => x.id === input.id && live(x)) : null;
  if (input.id && !s) throw new MediaError('not_found', 'That set is not available.');
  const patientUid = s ? s.patient_unique_id : (input.patient_unique_id === undefined ? null : input.patient_unique_id);
  requireChart(patientUid);
  const addIds = input.add_item_ids || [];
  const adds = addIds.map((id) => state.items.find((it) => it.id === id && live(it)));
  if (adds.some((it) => !it)) throw new MediaError('not_found', 'An item is no longer available.');
  if (adds.some((it) => it.patient_unique_id !== patientUid)) {
    throw new MediaError('invalid', 'Every item in a set must belong to the same patient.');
  }
  if (!s) {
    if (!String(input.name || '').trim()) throw new MediaError('invalid', 'Give the set a name.');
    s = newSetRow(state, patientUid, input.name, adds);
  } else {
    adds.forEach((it) => {
      if (!s.items.some((m) => m.media_id === it.id)) s.items.push({ media_id: it.id, position: s.items.length + 1 });
    });
    // Taking an item out of a set leaves it in the patient's library.
    const drop = input.remove_item_ids || [];
    if (drop.length) s.items = s.items.filter((m) => !drop.includes(m.media_id)).map((m, i) => ({ ...m, position: i + 1 }));
    if (input.name !== undefined) {
      if (!String(input.name).trim()) throw new MediaError('invalid', 'Give the set a name.');
      s.name = String(input.name).trim().slice(0, 150);
    }
  }
  if (input.set_date) s.set_date = input.set_date;
  if (input.progression_id !== undefined) {
    if (input.progression_id === null) s.progression_id = null;
    else {
      const p = state.progressions.find((x) => x.id === input.progression_id && live(x));
      if (!p) throw new MediaError('not_found', 'That progression is not available.');
      if (p.patient_unique_id !== patientUid) throw new MediaError('invalid', 'A progression only holds sets of its own patient.');
      s.progression_id = p.id;
    }
  }
  s.updated_at = new Date().toISOString();
  save(state);
  return shapeSet(state, s);
}

/** Future API_Meth ReorderSet: `itemIds` is the full new order. */
export async function reorderSet(setId, itemIds) {
  await tick();
  requireManage();
  const state = db();
  const s = state.sets.find((x) => x.id === setId && live(x));
  if (!s) throw new MediaError('not_found', 'That set is not available.');
  const current = s.items.map((m) => m.media_id).sort().join(',');
  if ([...itemIds].sort().join(',') !== current) throw new MediaError('invalid', 'The new order must contain exactly the set’s items.');
  s.items = itemIds.map((id, i) => ({ media_id: id, position: i + 1 }));
  s.updated_at = new Date().toISOString();
  save(state);
  return shapeSet(state, s);
}

/** Future API_Meth SaveProgression: create or rename. Sets join it through saveMediaSet. */
export async function saveProgression(input) {
  await tick();
  requireManage();
  const state = db();
  let p = input.id ? state.progressions.find((x) => x.id === input.id && live(x)) : null;
  if (!String(input.name || (p && p.name) || '').trim()) throw new MediaError('invalid', 'Give the progression a name.');
  if (!p) {
    requireChart(input.patient_unique_id);
    const pt = patientOf(input.patient_unique_id);
    p = {
      id: nextId(state), accountnumber: ACCOUNT, patient_pid: pt ? pt.pid : null, patient_unique_id: input.patient_unique_id,
      owner_unique_id: viewer.unique_ID, name: String(input.name).trim().slice(0, 150),
      primary_tag: input.primary_tag || null, body_site: input.body_site || null,
      created_at: new Date().toISOString(), removed_at: null,
    };
    state.progressions.push(p);
  } else if (input.name) {
    p.name = String(input.name).trim().slice(0, 150);
  }
  save(state);
  return shapeProgression(state, p);
}

/**
 * Future API_Meth ReassignMedia. A set moves with its items; an item moves alone. Refused with
 * `would_split` when a moving item is also in a set that stays with the old patient, naming it.
 * From the personal library this is "Assign to patient". A moved set leaves its progression.
 */
export async function reassignMedia(target, toPatientUid) {
  await tick();
  requireManage();
  requireChart(toPatientUid);
  const state = db();
  let moving;
  let movingSetIds;
  let fromUid;
  if (target.type === 'set') {
    const s = state.sets.find((x) => x.id === target.id && live(x));
    if (!s) throw new MediaError('not_found', 'That set is not available.');
    moving = setItems(state, s);
    movingSetIds = [s.id];
    fromUid = s.patient_unique_id;
  } else {
    const it = state.items.find((x) => x.id === target.id && live(x));
    if (!it) throw new MediaError('not_found', 'That item is not available.');
    moving = [it];
    movingSetIds = [];
    fromUid = it.patient_unique_id;
  }
  if (fromUid === toPatientUid) throw new MediaError('invalid', 'That media already belongs to this patient.');
  const movingIds = moving.map((it) => it.id);
  const blocking = state.sets.find((s) => live(s) && !movingSetIds.includes(s.id) && s.items.some((m) => movingIds.includes(m.media_id)));
  if (blocking) {
    const one = blocking.items.filter((m) => movingIds.includes(m.media_id)).length === 1;
    throw new MediaError('would_split', `“${blocking.name}” also holds ${one ? 'this item' : 'some of these items'} and stays with ${patientName(fromUid)}. Remove ${one ? 'it' : 'them'} from that set first.`, { blocking_set: { id: blocking.id, name: blocking.name } });
  }
  const to = patientOf(toPatientUid);
  moving.forEach((it) => { it.patient_unique_id = toPatientUid; it.patient_pid = to ? to.pid : null; });
  movingSetIds.forEach((id) => {
    const s = state.sets.find((x) => x.id === id);
    s.patient_unique_id = toPatientUid;
    s.patient_pid = to ? to.pid : null;
    s.progression_id = null;
  });
  state.reassignLog.push({
    id: nextId(state), accountnumber: ACCOUNT, target_type: target.type, target_id: target.id, item_ids: movingIds,
    from_pid: (patientOf(fromUid) || {}).pid || null, to_pid: to ? to.pid : null, by_unique_id: viewer.unique_ID, at: new Date().toISOString(),
  });
  save(state);
  return { moved: movingIds.length, to_patient_name: patientName(toPatientUid) };
}

/** Personal library → patient. Same rules as reassigning. */
export const assignToPatient = (target, patientUid) => reassignMedia(target, patientUid);

/** Future API_Meth ListPatientMedia { library: 'mine' }. */
export const listMyLibrary = (filters) => listPatientMedia(null, filters);

/**
 * Future API_Meth SaveMessageMedia. A message attachment lives in the conversation; saving it
 * copies the reference into the patient library as a media item.
 */
export async function saveMessageMediaToPatient(sourceId, storedFileId, patientUid) {
  await tick();
  requireManage();
  requireChart(patientUid);
  const state = db();
  const src = state.sources.find((s) => s.source_id === sourceId && s.source_type === 'message');
  const file = src && (src.message_files || []).find((f) => f.stored_file_id === storedFileId);
  if (!file) throw new MediaError('not_found', 'That attachment is not available.');
  if (file.saved_media_id) throw new MediaError('invalid', 'Already saved to the patient’s media.');
  const p = patientOf(patientUid);
  const id = nextId(state);
  const row = {
    id, public_id: `msg${String(id).padStart(29, '0')}`, accountnumber: ACCOUNT, patient_pid: p ? p.pid : null, patient_unique_id: patientUid,
    author_unique_id: viewer.unique_ID, captured_by_unique_id: file.sender_unique_id || null, kind: file.kind, content_type: file.content_type,
    width: 960, height: 720, duration_sec: null, captured_at: file.captured_at, capture_source: 'import', note: file.note || '',
    primary_tag: 'skin', tags: file.tags || [], body_site: file.body_site || null, body_site_detail: null, orddiagid: null, patient_visible: 0,
    created_at: new Date().toISOString(), removed_at: null, removed_by_unique_id: null, remove_reason: null, src: file.src, thumb_src: file.src,
  };
  state.items.push(row);
  file.saved_media_id = id;
  save(state);
  return shapeItem(row);
}

/** Prototype: wipe this browser's media data and go back to the seed, patients included. */
export async function resetLocal() {
  [...KINDS, PATIENTS_KEY].forEach((k) => {
    delete memory[k];
    try { window.localStorage.removeItem(`mediaProto:${k}`); } catch (e) { /* ignore */ }
  });
  blobMemory.clear();
  urlCache.forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* ignore */ } });
  urlCache.clear();
  const d = await idb();
  if (d) {
    await new Promise((resolve) => {
      try {
        const tx = d.transaction('blobs', 'readwrite');
        tx.objectStore('blobs').clear();
        tx.oncomplete = resolve;
        tx.onerror = resolve;
      } catch (e) { resolve(); }
    });
  }
  db();
}
